import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import useSWR from 'swr';
import BrandGrid from '@/components/brand-grid';
import { Button, Section, SectionTitle } from '@/components/ui';
import config from '@/config';
import { countryNameInSentence } from '@/utils/country-name';

const MAX_BRANDS = 12;

const Title = styled(SectionTitle)`
  margin-bottom: 32px;
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const urlForCountry = (countryId) =>
  `${config.commerceApiUrl}/search?countryId=${countryId}&type=giftcard&minimal=true`;

// One more than shown, so the current brand can be dropped from the list.
const brandsFromResult = (result) =>
  result?.groups?.length > 0 ? result.groups[0].categories[0].brands.slice(0, MAX_BRANDS + 1) : [];

const fetcher = (...args) =>
  fetch(...args)
    .then((res) => res.json())
    .then(brandsFromResult);

/**
 * `initialBrands` comes from the build-time catalogue (getServerSideProps of the
 * brand page, no network), so the links are in the server HTML. Only when the
 * build data knows nothing about the country (null) are the brands fetched here,
 * in the browser, once the section scrolls into view. Never fetch this list
 * during the server render: it is the country's whole catalogue.
 */
const OtherBrandsInCountry = ({ brand, initialBrands = null }) => {
  const { country, id: currentBrandId } = brand;
  const t = useTranslations('otherBrandsInCountry');
  const countryT = useTranslations('country');
  const { locale } = useRouter();
  const countryName = countryNameInSentence(country, locale);
  const hasInitialBrands = Array.isArray(initialBrands);
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: '100px' });
  const { data } = useSWR(!hasInitialBrands && inView ? urlForCountry(country.id) : null, fetcher);
  const brands = hasInitialBrands ? initialBrands : data;
  const brandsToShow = (brands || []).filter((b) => b.id !== currentBrandId).slice(0, MAX_BRANDS);
  // The build data lists top-ups and vouchers after the gift cards: only call the
  // list "gift cards" when that is all it holds (same wording as the country page).
  const giftCardsOnly = brandsToShow.every((b) => !b.type || b.type === 'giftcard');
  const title = giftCardsOnly
    ? t('title', { country: countryName })
    : countryT('brandsTitle', { country: countryName });
  const viewAll = giftCardsOnly
    ? t('viewAll', { country: countryName })
    : countryT('browseAllBrands', { country: countryName });

  return (
    <div ref={ref} style={{ minHeight: 1 }}>
      {brandsToShow.length > 0 && (
        <Section id="other-brands" $spacing="compact">
          <Title>{title}</Title>
          <BrandGrid brands={brandsToShow} />
          <Actions>
            <Button $size="pill" $variant="outline" href={`/buy/${country.slug}`}>
              {viewAll}
            </Button>
          </Actions>
        </Section>
      )}
    </div>
  );
};

export default OtherBrandsInCountry;
